import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Alert,
  Box,
} from "@mui/material";
import { consultarCancelamentoBpe, cancelarBpe } from "../../api/bpeApi";

const BPeCancelDialog = ({ open, onClose, conversationId, ticket, onCanceled }) => {
  const { t } = useTranslation("chat");
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);

  const identificador =
    ticket?.reservationId || ticket?.id || ticket?.localizador || ticket?.numeroBPe || "";

  useEffect(() => {
    if (!open || !conversationId || !identificador) return;
    setLoading(true);
    setError(null);
    setInfo(null);
    consultarCancelamentoBpe(conversationId, { identificador_reserva: identificador })
      .then((data) => setInfo(data))
      .catch((e) => setError(e.message || t("passagensBpe.cancelConsultError")))
      .finally(() => setLoading(false));
  }, [open, conversationId, identificador, t]);

  const handleConfirmCancel = async () => {
    setConfirming(true);
    setError(null);
    try {
      const result = await cancelarBpe(conversationId, {
        identificador_reserva: identificador,
        valor_a_reembolsar: info?.valor_a_reembolsar,
      });
      if (result?.estorno?.error) {
        setError(result.estorno.error);
      } else if (result?.estorno?.mensagem) {
        setInfo((prev) => ({ ...prev, estornoMsg: result.estorno.mensagem }));
      }
      if (onCanceled) onCanceled();
      if (!result?.estorno?.error) onClose();
    } catch (e) {
      setError(e.message || t("passagensBpe.cancelError"));
    } finally {
      setConfirming(false);
    }
  };

  const canCancel = info?.permitido !== false && info?.status !== false;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("passagensBpe.cancelTitle")}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          {ticket?.origem} → {ticket?.destino}
          {ticket?.localizador && ` · ${t("passagensBpe.locator")}: ${ticket.localizador}`}
        </Typography>
        {loading && (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        )}
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {info && !loading && (
          <Box mt={1}>
            {info.permitido === false ? (
              <Alert severity="warning">{t("passagensBpe.cancelNotAllowed")}</Alert>
            ) : (
              <>
                {info.custo_reembolso != null && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t("passagensBpe.cancelFee")}: R$ {info.custo_reembolso}
                  </Typography>
                )}
                {info.valor_a_reembolsar != null && (
                  <Typography variant="body2">
                    {t("passagensBpe.cancelRefund")}: R$ {info.valor_a_reembolsar}
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  {t("passagensBpe.cancelConfirmHint")}
                </Typography>
              </>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={confirming}>
          {t("bpeWizard.cancel")}
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleConfirmCancel}
          disabled={loading || confirming || !canCancel}
        >
          {confirming ? <CircularProgress size={20} color="inherit" /> : t("passagensBpe.cancelConfirm")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BPeCancelDialog;
